import { useContext, useState } from 'react';
import { Alert, Button, Glyphicon } from 'react-bootstrap';
import { HeaderWrapper, styles } from './styles';
import { RecipientProfileContext, ToggleType } from './models';
import { Icon } from 'react-icons-kit';
import { ic_brightness_1 } from 'react-icons-kit/md/ic_brightness_1';
import { MatchStatus, PatientType } from '@nkr/enums/index';

interface IProps {
    onOrderTypingKit: () => void
}

const RecipientProfileHeaderInfo = (props: IProps) => {
    const context = useContext(RecipientProfileContext);

    const [expand, setExpand] = useState(false);

    const renderDivider = () => {
        return <div className='item-divider'><Icon size={10} icon={ic_brightness_1} /></div>;
    };

    if (context.headerInfo == null) {
        return null;
    }

    let isWaitList = context.headerInfo.typeId == PatientType.Waitlist;

    let isComplete = true;

    if (!isWaitList) {
        isComplete = context.headerInfo.isExportable && context.headerInfo.validationResults.length == 0;
    } else {
        isComplete = context.headerInfo.waitListReplaceResults.length == 0;
    }

    let showSwapInfo = context.headerInfo.clusterId != 0 && context.headerInfo.chainId != 0;
    let showRequiredFieldsForExportability = context.headerInfo.typeId != PatientType.Waitlist && context.headerInfo.validationResults.length > 0;
    const showNotReadyForMatching =!isWaitList && !context.headerInfo.isExportable && context.headerInfo.validationResults.length == 0;
    let showExcludedFromChainEndAllocation = context.headerInfo.typeId == PatientType.Waitlist && context.headerInfo.waitListReplaceResults.length > 0;
    let showTissueTypingNotice = context.headerInfo.histogeneticsRawTypingFile != null && context.headerInfo.histogeneticsRawTypingFile.length <= 5;

    return (
        <div style={{ marginBottom: 20 }}>
            {context.toggleType == ToggleType.Success && (
                <Alert bsStyle='success' bsSize='sm'>
                    Save completed successfully
                </Alert>
            )}

            {context.showCheekOrderNotification && (
                <Alert bsStyle='success'>
                    <div style={{ display: 'flow-root' }}>
                        <span style={{ float: 'left' }}>
                            <strong>Please click the 'Recipient Actions' button, in order to do the following: </strong>
                            <ul style={{ paddingInlineStart: 20 }}>
                                <li>
                                    Order High Res Tissue Typing (Cheek Swab)
                                </li>
                                <li>
                                    Utilize a Bulk Cheek Swab (Kit on hand)
                                </li>
                            </ul>
                        </span>
                        <span style={{ float: 'right' }}>
                            <Button onClick={() => context.onCheekOrderNotify(false)}>
                                X
                            </Button>
                        </span>
                    </div>
                </Alert>
            )}

            {context.toggleType == ToggleType.Errors && context.messages.length > 0 && (
                <Alert bsStyle='danger' bsSize='sm'>
                    <strong>The recipient record was NOT saved. Please fix the following errors and try again:</strong>
                    <ul style={{ paddingInlineStart: 20 }}>
                        {context.messages.map(message => <li>{message}</li>)}
                    </ul>
                </Alert>
            )}

            {context.toggleType == ToggleType.Warnings && context.messages.length > 0 && (
                <Alert bsStyle='warning' bsSize='sm'>
                    <strong>The recipient record was saved but be aware of the following issues:</strong>
                    <ul style={{ paddingInlineStart: 20 }}>
                        {context.messages.map(message => <li>{message}</li>)}
                    </ul>
                </Alert>
            )}

            <HeaderWrapper className='well well-sm'>
                <div className='donor-progress-bar'>
                    <div className='header-left' onClick={() => setExpand(!expand)}>
                        <Button block>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: 10, fontSize: 16, fontWeight: 'bold' }}>{context.headerInfo.alias}</div>
                                <div>
                                    <div>{context.headerInfo.isExportable ? <>Matchable</> : <>Will Not Be Matched</>}</div>
                                    <div style={{ fontSize: 12, lineHeight: '12px' }}>(click to expand)</div>
                                </div>
                            </div>
                        </Button>
                    </div>

                    <div style={{ paddingLeft: 0, paddingRight: 15 }}><Glyphicon glyph='chevron-right' /></div>

                    <div className='header-buttons'>
                        <div className='item-button'>
                            {!context.headerInfo.lastOrderDate && !context.headerInfo.alleleData &&
                                <Button block bsStyle='danger' onClick={() => context.onCheekOrderNotify(true)}>
                                    <div>ORDER CHEEK SWAB KIT</div>
                                    <div style={{ fontSize: 12, lineHeight: '12px' }}>(no cheek swab ordered for this recipient)</div>
                                </Button>
                            }

                            {context.headerInfo.lastOrderDate && !context.headerInfo.alleleData &&
                                <Button block bsStyle='warning' onClick={() => context.onCheekOrderNotify(true)}>
                                    <div>ORDER CHEEK SWAB KIT</div>
                                    <div style={{ fontSize: 12, lineHeight: '12px' }}>(cheek swab last ordered {context.headerInfo.lastOrderDate})</div>
                                </Button>
                            }

                            {context.headerInfo.alleleData &&
                                <Button block bsStyle='success' onClick={() => context.onCheekOrderNotify(true)}>
                                    <div>ORDER CHEEK SWAB KIT</div>
                                    <div style={{ fontSize: 12, lineHeight: '12px' }}>(received results)</div>
                                </Button>
                            }
                        </div>

                        {renderDivider()}

                        <div className='item-button'>
                            {isComplete ?
                                <Button block bsStyle='success' onClick={() => setExpand(!expand)}>
                                    <div>{isWaitList == false ? 'Required Fields for Exportability' : 'Required Fields for Wait List'}</div>
                                    <div style={{ fontSize: 12, lineHeight: '12px' }}>(all requirements have been met)</div>
                                </Button>
                                :
                                <Button block bsStyle='danger' onClick={() => setExpand(!expand)}>
                                    <div>{isWaitList == false ? 'Required Fields for Exportability' : 'Failed Wait List Criteria'}</div>
                                    <div style={{ fontSize: 12, lineHeight: '12px' }}>({showNotReadyForMatching ? 'not flagged for matching - click to expand' : (isWaitList == false ? context.headerInfo.validationResults.length : context.headerInfo.waitListReplaceResults.length) + ' issue(s) found'})</div>
                                </Button>
                            }
                        </div>

                        {renderDivider()}

                        <div className='item-button'>
                            {isComplete ?
                                <Button block bsStyle={!isWaitList ? 'success' : 'warning'}>
                                    <div>Complete</div>
                                    <div style={{ fontSize: 12, lineHeight: '12px' }}>({!isWaitList ? 'ready for matching' : 'waitlist patients excluded from matching'})</div>
                                </Button>
                                :
                                <Button block bsStyle='danger'>
                                    <div>Incomplete</div>
                                    <div style={{ fontSize: 12, lineHeight: '12px' }}>({!isWaitList ? 'not ready for matching' : 'not available for automated chain-end offers'})</div>
                                </Button>
                            }
                        </div>
                    </div>
                </div>

                {expand && (
                    <div className='donor-progress-messages'>
                        {showSwapInfo &&
                            <Alert bsStyle='warning' bsSize='sm' style={{ marginBottom: 5 }}>
                                <div style={{ display: 'flex' }}>
                                    <div style={{ marginRight: 10 }}><strong>Chain:</strong> {context.headerInfo.chainId} ({context.headerInfo.chainStatus})</div>
                                    <div style={{ marginRight: 10 }}><strong>Cluster:</strong> {context.headerInfo.clusterNumber} ({context.headerInfo.clusterStatus})</div>
                                    <div><strong>Position:</strong> {context.headerInfo.positionInSwap}</div>
                                </div>
                            </Alert>
                        }

                        <Alert bsSize='sm' style={{ marginBottom: showRequiredFieldsForExportability || showNotReadyForMatching || showExcludedFromChainEndAllocation ? 5 : 0 }}>
                            <h4>CHEEK SWAB KIT ORDER STATUS</h4>
                            {context.headerInfo.lastOrderDate ?
                                <div>A cheek swab was last ordered {context.headerInfo.lastOrderDate}</div>
                                :
                                <div>No cheek swab ordered for this recipient.</div>
                            }
                        </Alert>

                        {showRequiredFieldsForExportability &&
                            <Alert bsSize='sm' style={{ marginBottom: showExcludedFromChainEndAllocation ? 5 : 0 }}>
                                <h4>REQUIRED FIELDS FOR EXPORTABILITY</h4>
                                <div style={{ marginBottom: 5 }}>
                                    {context.headerInfo.statusId == MatchStatus.NeedsMatch ?
                                        'This recipient will not be included in match runs for the following reasons:' :
                                        'This patient is already in a match run, but we still need the following information:'}
                                </div>
                                <ul style={{ paddingInlineStart: 20 }}>
                                    {context.headerInfo.validationResults.map((item, index) =>
                                        <li style={{ listStyleType: 'disc' }} key={'vad-' + index}>{item}</li>
                                    )}
                                </ul>
                            </Alert>
                        }

                        {showNotReadyForMatching &&
                            <Alert bsSize='sm' style={showTissueTypingNotice ? styles.progressMessage : styles.progressMessageLast}>
                                <h4>NOT READY FOR MATCHING</h4>
                                <div style={styles.progressMessageIntro}>This recipient has not been flagged as exportable by the matching system, but no specific issue was found. Possible things to check:</div>
                                <ul style={styles.progressMessageList}>
                                    <li style={styles.progressMessageListItem}>Save the recipient record. Some changes, such as updates to a directed donor, are not picked up until the recipient is saved.</li>
                                    <li style={styles.progressMessageListItem}>Voucher holders, including Swap Saver and RTSF, must have their voucher status set to Active.</li>
                                    <li style={styles.progressMessageListItem}>Paired recipients must have a directed donor with all required fields complete.</li>
                                </ul>
                            </Alert>
                        }

                        {showExcludedFromChainEndAllocation &&
                            <Alert bsSize='sm' style={{ marginBottom: showTissueTypingNotice ? 5 : 0 }}>
                                <h4>EXCLUDED FROM CHAIN-END OFFER</h4>
                                <div style={{ marginBottom: 5 }}>
                                    This waitlist patient will not be available for automated chain-end offers because:
                                </div>
                                <ul style={{ paddingInlineStart: 20 }}>
                                    {context.headerInfo.waitListReplaceResults.map((item, index) =>
                                        <li style={{ listStyleType: 'disc' }} key={'vad-' + index}>{item}</li>
                                    )}
                                </ul>
                            </Alert>
                        }

                        {showTissueTypingNotice &&
                            <Alert bsSize='sm' style={{ marginBottom: 0 }}>
                                <h4>IMPORTANT TISSUE TYPING NOTICE</h4>
                                <div style={{ marginBottom: 5 }}>
                                    This patient does not have the NKR ABO confirmatory typing on file.
                                    ABO confirmatory typing is now required. As an added benefit, this will
                                    provide high resolution tissue typing. This ensures that your patient
                                    has access to the best possible matching opportunities.&nbsp;
                                    <a style={{ cursor: 'default', fontWeight: 'bold', textDecoration: 'underline' }} onClick={props.onOrderTypingKit}>Click here</a> to order a kit.
                                </div>
                            </Alert>
                        }
                    </div>
                )}
            </HeaderWrapper>
        </div>
    );
}

export default RecipientProfileHeaderInfo;